import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import ThSearch from '../../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import DateTimeCell from '../../CmdTable/TableCells/DateTimeCell';
import ThOnlySort from '../../CmdTable/TableHeadCell/ThOnlySort';
import ThMultiSelect from '../../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../../CmdTable/TableCells/TextCell';

import {
  BLE_USAGE_TABLE_NAME,
  CUSTOMER_KEY,
  MAC_ADDRESS_KEY,
  MANUFACTURER_KEY,
  MODEL_KEY,
  NAME_KEY,
  SERIAL_NUMBER_KEY,
  USAGE_DURATION_KEY,
  USAGE_END_ADDRESS_KEY,
  USAGE_END_KEY,
  USAGE_START_ADDRESS_KEY,
  USAGE_START_COORDINATES_KEY,
  USAGE_START_KEY,
  USAGE_TIMESTAMP_KEY,
  UUID_KEY,
  VEHICLE_KEY,
  VEHICLE_LICENCE_NUMBER_KEY,
} from '../constants';
import ThText from '../../CmdTable/TableHeadCell/ThText';
import LinkCell from '../../CmdTable/TableCells/LinkCell';
import { BluecoinUsageRecord } from './BluecoinsUsageState';
import ThRange from '../../CmdTable/TableHeadCell/ThRange';
import { customersAtom, selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../../CmdTable/CmdTableState';

interface IUseBleUsage {
  columns: ColumnDef<BluecoinUsageRecord>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useBleUsage(): IUseBleUsage {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  // const tableFiltersKey = useRecoilValue(CmdTableState.tableFilters);
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(BLE_USAGE_TABLE_NAME)
  );
  const customer = useRecoilValue(customersAtom);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(BLE_USAGE_TABLE_NAME)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);

  // const updateTable = React.useCallback(() => {
  //   queryClient.invalidateQueries({
  //     queryKey: [BLE_USAGE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
  //   });
  // }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  const columns = React.useMemo<ColumnDef<BluecoinUsageRecord>[]>(
    () => [
      {
        accessorKey: UUID_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_USAGE_TABLE_NAME}
            id={UUID_KEY}
            title={f({ id: `table_list.columns.bluecoinUsage.${UUID_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoinUsage.${UUID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.uuid ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: MAC_ADDRESS_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_USAGE_TABLE_NAME}
            id={MAC_ADDRESS_KEY}
            title={f({ id: `table_list.columns.bluecoinUsage.${MAC_ADDRESS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoinUsage.${MAC_ADDRESS_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.macAddress ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        // size: 260,
        minSize: 200,
      },
      {
        accessorKey: SERIAL_NUMBER_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_USAGE_TABLE_NAME}
            id={SERIAL_NUMBER_KEY}
            title={f({ id: `table_list.columns.bluecoinUsage.${SERIAL_NUMBER_KEY}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.serialNumber ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 80,
      },
      {
        accessorKey: MANUFACTURER_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_USAGE_TABLE_NAME}
            id={MANUFACTURER_KEY}
            title={f({ id: `table_list.columns.bluecoinUsage.${MANUFACTURER_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoinUsage.${MANUFACTURER_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.manufacturer ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 100,
      },
      {
        accessorKey: MODEL_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_USAGE_TABLE_NAME}
            id={MODEL_KEY}
            title={f({ id: `table_list.columns.bluecoinUsage.${MODEL_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoinUsage.${MODEL_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.model ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: NAME_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={BLE_USAGE_TABLE_NAME}
            id={NAME_KEY}
            title={f({ id: `table_list.columns.bluecoinUsage.${NAME_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoinUsage.${NAME_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.name ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_LICENCE_NUMBER_KEY,
        header: (table) => (
          <ThMultiSelect
            id={VEHICLE_LICENCE_NUMBER_KEY}
            tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${VEHICLE_LICENCE_NUMBER_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoinUsage.${VEHICLE_LICENCE_NUMBER_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicle ? (
            <TextCell text={info.row.original?.vehicle.licenceNumber || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_KEY,
        header: (table) => (
          <ThMultiSelect
            id={VEHICLE_KEY}
            tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${VEHICLE_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoinUsage.${VEHICLE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicle ? (
            <TextCell
              text={
                info.getValue<{
                  id: number | null;
                  licenceNumber: string | null;
                  name: string | null;
                }>()?.name || ''
              }
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: USAGE_TIMESTAMP_KEY,
        header: (table) => (
          <ThDateTime
            id={USAGE_TIMESTAMP_KEY}
            tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${USAGE_START_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoinUsage.${USAGE_START_KEY}.tooltip` })}
            isSortable={true}
            alwaysActive
          />
        ),
        cell: (info) =>
          info.row.original?.timestamp ? (
            <DateTimeCell
              datetime={info.getValue<{ start: string; end: string }>().start || ''}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: USAGE_END_KEY,
        header: (table) => (
          <ThDateTime
            id={USAGE_END_KEY}
            tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${USAGE_END_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoinUsage.${USAGE_END_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.usageEnd ? (
            <DateTimeCell
              datetime={
                info.getValue<{
                  timestamp: string | null;
                  address: string | null;
                  addressLink: string | null;
                  coordinates: string | null;
                  coordinatesLink: string | null;
                }>().timestamp || ''
              }
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: USAGE_DURATION_KEY,
        header: (table) => (
          <ThRange
            tableName={BLE_USAGE_TABLE_NAME}
            id={USAGE_DURATION_KEY}
            title={f({ id: `table_list.columns.bluecoinUsage.${USAGE_DURATION_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoinUsage.${USAGE_DURATION_KEY}` })}
            isSortable={true}
            unit="min"
          />
        ),
        cell: (info) =>
          info.row.original?.duration ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: USAGE_START_ADDRESS_KEY,
        header: (table) => (
          <ThText
            // id={USAGE_START_ADDRESS_KEY}
            // tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${USAGE_START_ADDRESS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoinUsage.${USAGE_START_ADDRESS_KEY}.tooltip` })}
          />
        ),
        cell: (info) =>
          info.row.original?.usageStart ? (
            <LinkCell
              text={info.row.original?.usageStart?.address || ''}
              link={info.row.original?.usageStart?.addressLink || ''}
              // tooltipEnabled={!!info.row.original?.usageStart?.address}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 290,
      },
      {
        accessorKey: USAGE_END_ADDRESS_KEY,
        header: (table) => (
          <ThText
            // id={USAGE_END_ADDRESS_KEY}
            // tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${USAGE_END_ADDRESS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoinUsage.${LAST_USAGE_ADDRESS_KEY}.tooltip` })}
          />
        ),
        cell: (info) =>
          info.row.original?.usageEnd ? (
            <LinkCell
              text={info.row.original?.usageEnd?.address || ''}
              link={info.row.original?.usageEnd?.addressLink || ''}
              // tooltipEnabled={!!info.row.original?.lastUsage?.address}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 290,
      },
      {
        accessorKey: USAGE_START_COORDINATES_KEY,
        header: (table) => (
          <ThText
            // id={USAGE_START_COORDINATES_KEY}
            // tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${USAGE_START_COORDINATES_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoinUsage.${LAST_USAGE_ADDRESS_KEY}.tooltip` })}
          />
        ),
        cell: (info) =>
          info.row.original?.usageStart ? (
            <LinkCell
              text={info.row.original?.usageStart?.coordinates || ''}
              link={info.row.original?.usageStart?.coordinatesLink || ''}
              // tooltipEnabled={!!info.row.original?.lastUsage?.address}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 260,
      },
      {
        accessorKey: CUSTOMER_KEY,
        header: (table) => (
          <ThOnlySort
            id={CUSTOMER_KEY}
            tableName={BLE_USAGE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoinUsage.${CUSTOMER_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoinUsage.${CUSTOMER_KEY}.tooltip` })}
            isSortable
          />
        ),
        cell: (info) =>
          info.row.original?.customer ? (
            <TextCell
              text={
                info.getValue<{
                  id: number;
                  name: string | null;
                }>().name || ''
              }
              // tooltipEnabled={true}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      // {
      //   accessorKey: ACTIONS_KEY,
      //   header: () => (
      //     <div className="w-sm text-center th-sticky sticky-col detail">
      //       {f({ id: 'table.deleteBtn' })}
      //     </div>
      //   ),
      //   cell: (info) =>
      //     info.row.original ? <Actions<BluecoinUsageRecord> data={info.row.original} /> : null,
      //   size: 80, // Adjusted for consistency with header styling
      //   enablePinning: true,
      // },
    ],
    [f]
  );

  const columnOrder = React.useMemo(() => {
    const order = [...columnsTable.map((item) => item.key)];
    // order.push(ACTIONS_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
